import React, { useEffect, useRef, useState } from 'react';
import type { User } from '@supabase/supabase-js';
import { supabase } from '../supabaseClient';
import { useCompany } from '../CompanyContext';

interface Props {
  user: User;
  children: React.ReactNode;
}

// Puerta del área autenticada: una sesión sin fila en profiles (p. ej. una
// invitación que no se terminó de aceptar) ya no ve un tablero vacío con todo
// el menú, sino un mensaje claro. Solo actúa mientras NO hay perfil: los
// refresh() posteriores (con perfil ya cargado) no desmontan el Dashboard.
export default function RequiereEmpresa({ user, children }: Props) {
  const { profile, loading, refresh } = useCompany();
  const [saliendo, setSaliendo] = useState(false);
  const reintentos = useRef(0);

  // App.tsx crea la empresa (registro) o acepta la invitación pendiente en
  // onAuthStateChange; si el perfil se leyó justo antes de que terminara, se
  // vuelve a leer sola un par de veces antes de dejar el mensaje.
  useEffect(() => {
    if (profile || loading || reintentos.current >= 2) return;
    const espera = reintentos.current === 0 ? 1500 : 4000;
    const t = setTimeout(() => { reintentos.current += 1; refresh(); }, espera);
    return () => clearTimeout(t);
  }, [profile, loading, refresh]);

  if (profile) return <>{children}</>;

  // Mientras carga o quedan reintentos automáticos, no se muestra el aviso.
  if (loading || reintentos.current < 2) {
    return (
      <div style={wrap}>
        <div style={card}>
          <p style={brand}>StorSafe</p>
          <p style={muted}>Cargando tu cuenta…</p>
        </div>
      </div>
    );
  }

  async function cerrarSesion() {
    setSaliendo(true);
    await supabase.auth.signOut();
  }

  return (
    <div style={wrap}>
      <div style={card}>
        <p style={brand}>StorSafe</p>
        <h1 style={titleStyle}>Tu cuenta no tiene empresa</h1>
        <p style={muted}>Iniciaste sesión como <strong style={{ color: '#e2e8f0' }}>{user.email}</strong>.</p>
        <p style={body}>
          Esta cuenta todavía no está vinculada a ninguna empresa en StorSafe.
        </p>
        <ul style={list}>
          <li>Si te invitaron, abre de nuevo el enlace de invitación que llegó a tu correo.</li>
          <li>Si acabas de registrar tu empresa, pulsa «Reintentar».</li>
          <li>Si el problema continúa, escríbenos a <a href="mailto:info@storsafe.app" style={link}>info@storsafe.app</a>.</li>
        </ul>
        <div style={actions}>
          <button type="button" style={btn} onClick={() => { refresh(); }}>Reintentar</button>
          <button type="button" style={btnSecondary} onClick={cerrarSesion} disabled={saliendo}>
            {saliendo ? 'Cerrando…' : 'Cerrar sesión'}
          </button>
        </div>
      </div>
    </div>
  );
}

const wrap: React.CSSProperties = { minHeight: '100vh', background: '#0f172a', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: '64px 16px', boxSizing: 'border-box' };
const card: React.CSSProperties = { background: '#1e293b', border: '1px solid #334155', borderRadius: 16, padding: 28, width: '100%', maxWidth: 480, textAlign: 'center' };
const brand: React.CSSProperties = { color: '#2FBF71', fontWeight: 800, letterSpacing: '0.04em', margin: '0 0 16px' };
const titleStyle: React.CSSProperties = { color: '#f1f5f9', fontSize: '1.3rem', fontWeight: 700, margin: '0 0 8px' };
const muted: React.CSSProperties = { color: '#94a3b8', fontSize: '0.9rem', margin: 0, overflowWrap: 'anywhere' };
const body: React.CSSProperties = { color: '#e2e8f0', lineHeight: 1.6, margin: '16px 0 8px' };
const list: React.CSSProperties = { color: '#cbd5e1', lineHeight: 1.6, textAlign: 'left', margin: '0 0 24px', paddingLeft: 20, fontSize: '0.9rem' };
const link: React.CSSProperties = { color: '#2FBF71' };
const actions: React.CSSProperties = { display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' };
const btn: React.CSSProperties = { background: '#2FBF71', color: '#0f172a', fontWeight: 700, padding: '10px 20px', borderRadius: 8, border: 'none', cursor: 'pointer' };
const btnSecondary: React.CSSProperties = { background: 'transparent', color: '#e2e8f0', fontWeight: 600, padding: '10px 20px', borderRadius: 8, border: '1px solid #475569', cursor: 'pointer' };
